import {EditorView} from '@codemirror/view';
import {
  CodeEditor,
  type CodeMirror,
  type SyntaxError,
  formatWithPrettier,
  skipOnChange,
  useSpringCm6Theme,
  validateSyntax,
  PrettifySyntaxErrorModal,
} from '@packages/systems/code-mirror';
import {
  selectComponentCanvasCodeComponentSource,
  selectComponentCanvasComponentId,
} from '@packages/systems/component-canvas/designer';
import {
  ArrowDownIcon,
  Button,
  Card,
  CloseDefaultIcon,
  IconButton,
  InfoIcon,
  LockIcon,
  Note,
  NoteBody,
  SidebarCollapseIcon,
  SidebarExpandIcon,
  Text,
  View,
  VisuallyHidden,
  WarningTriangleIcon,
} from '@packages/systems/spring';
import {emotionStyled, tokens} from '@packages/utilities/styled';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  selectActiveSourceFile,
  selectCodeComponentDraftSaveInput,
  selectDraftsClearedVersion,
  selectIsSandboxRebuilding,
  selectSandboxJobStatus,
  selectRefusedSourceFileKeys,
  selectSourceFileDrafts,
  selectSourceFileSaveRefusalVersion,
  selectVCCInstantApplyHighlight,
  selectVCCSelectedSourceRange,
  useCodeComponentDispatch,
  useCodeComponentSelector,
  useCodeComponentSourceFileSelected,
  useCodeComponentUpdated,
  useSourceFileContentSaved,
} from './flux/CodeLibrariesSlice';
import {
  INSTANT_APPLY_HIGHLIGHT_DURATION_MS,
  instantApplyHighlightExtension,
  setInstantApplyHighlight,
} from './instantTextApply/instantApplyHighlightExtension';
import {
  selectedElementHighlightExtension,
  setSelectedElementHighlight,
} from './selectionReveal/selectedElementHighlightExtension';
// eslint-disable-next-line webflow/module-top-level-imports
import {
  selectCodeStreamingState,
  selectIsAgentTurnInProgress,
} from '@packages/systems/ai/assistant-next/frontend/selectors';
import {ANALYTICS_EVENTS} from '@packages/systems/core/constants/analytics';
import {trackEvent} from '@packages/utilities/analytics';
import {selectDesignerAbility} from '@packages/systems/designer-abilities';
import {selectIsOnPrimaryLocale} from '@packages/systems/localization';
import {getIsBranchMode} from '@packages/systems/designer-selectors';
import {
  selectSiteId,
  selectWorkspaceId,
} from '@packages/systems/designer/site-data';
import {
  useDesignerSelector,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import {
  codeComponentSourceDraftChanged,
  codeComponentEditorPaneHide,
  codeComponentSourceFileSaveRefused,
} from './flux/actionCreators';
import {mainSourceDraftKey} from './flux/CodeComponentUIState';
import {sourceFileKey} from '@packages/domains/code-libraries/data-types';
import {ensureCodeChangesConfirmed} from './flux/ensureCodeChangesConfirmed';
import {saveCodeComponentDrafts} from './flux/saveCodeComponentDrafts';
import {
  refusedSaveReason,
  sourceFileSaveRefusal,
} from './flux/validateSourceFileSave';
import {registerCodeComponentDraftFlush} from './flux/codeComponentDraftRegistry';
import {extensionOf, getSourceFileFormatParser} from './sourceFileFormatParser';
import {
  PasteHarnessNotice,
  pasteDetectExtension,
  usePasteAutoHarness,
  useTakenComponentNames,
} from './startFromCode';
import {useSourceTreePeek} from './useSourceTreePeek';
import {useFeatureFlag} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';

const selectUserId = (state: DesignerStores): string =>
  state.SiteDataStore.userId;

const selectIsOnPrimaryLocaleFromStores = (state: DesignerStores): boolean =>
  selectIsOnPrimaryLocale(state.LocalizationStore);

const selectIsBranchMode = (state: DesignerStores): boolean =>
  getIsBranchMode(state.DesignerStore);

const cardStyle = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
} as const;

const codeEditorStyle = {
  borderRadius: 0,
} as const;

const SOURCE_TREE_WIDTH_PX = 200;
/** Width of the left-edge strip that reveals a collapsed tree on hover. */
const SOURCE_TREE_PEEK_ZONE_WIDTH_PX = 8;
const SOURCE_TREE_ID = 'vcc-source-file-tree';

// Holds the source-file tree and the editor side by side beneath the pane's
// single header, so the two read as one pane instead of two stacked cards. Owns
// the Card's bottom rounding (the tree occupies the bottom-left corner now) and
// clips the tree while it slides in and out.
const EditorBodyRow = emotionStyled('div')({
  position: 'relative',
  display: 'flex',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  borderBottomLeftRadius: tokens.radii[2],
  borderBottomRightRadius: tokens.radii[2],
  // The pane is being dragged or resized: nothing inside it should react,
  // including the tree's rows and its peek strip.
  '&[data-locked="true"]': {pointerEvents: 'none'},
});

// Expanded, the tree is a fixed-width column in the row. Collapsed, it leaves
// the layout so the editor takes the full width, and slides back in over the
// editor for as long as the user peeks at it. `visibility` (rather than
// `pointerEvents`) is what keeps a fully collapsed tree out of the tab order,
// and transitions alongside `transform` so it stays visible on the way out.
const SourceTreeSlot = emotionStyled('div')({
  width: SOURCE_TREE_WIDTH_PX,
  flex: '0 0 auto',
  height: '100%',
  overflow: 'hidden',
  borderRight: `1px solid ${tokens.colors.border1}`,
  background: tokens.colors.background1,
  '&[data-collapsed="true"]': {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    height: 'auto',
    // The one z-index here: the editor and the peek strip come later in the DOM
    // and would otherwise paint over the tree while it is sliding in. DOM order
    // can't solve it - the tree has to stay the first child so focus order
    // matches the visual column order when it is expanded.
    zIndex: 1,
    transform: `translateX(-${SOURCE_TREE_WIDTH_PX}px)`,
    visibility: 'hidden',
    transition: 'transform 200ms ease-out, visibility 200ms',
  },
  '&[data-collapsed="true"][data-peeking="true"]': {
    transform: 'translateX(0)',
    visibility: 'visible',
    boxShadow: tokens.boxShadows[3],
  },
  '@media (prefers-reduced-motion: reduce)': {
    '&[data-collapsed="true"]': {transition: 'none'},
  },
});

// Rendered last in the row so it paints over the editor without a z-index of
// its own, and still under the collapsed tree, which carries one. That order
// matters: over the editor so it can be hovered at all, and under the tree so
// that once the tree slides over it the pointer is on the tree itself -
// otherwise the tree's own mouseleave fires against this strip and the peek
// flickers. It holds no content and takes no focus, so being last in the DOM
// costs nothing.
const SourceTreePeekZone = emotionStyled('div')({
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  width: SOURCE_TREE_PEEK_ZONE_WIDTH_PX,
});

// Stable identity — the CodeEditor only reads extensions at construction.
const codeEditorExtensions = [
  instantApplyHighlightExtension,
  selectedElementHighlightExtension,
  pasteDetectExtension,
];

/**
 * Where a revealed line lands vertically in the pane. Matches the instant-apply
 * reveal below so a scroll from a canvas selection and a scroll from an applied
 * edit put the target in the same place.
 */
const REVEAL_VIEWPORT_FRACTION = 0.35;

type EditorNoteContext = {
  isAgentBusy: boolean;
  isRebuilding: boolean;
  canEditCodeComponents: boolean;
  isBranchMode: boolean;
  isOnPrimaryLocale: boolean;
  saveRejection: string | null;
};

type EditorNoteConfig = {
  icon: React.ComponentProps<typeof Note>['icon'];
  message: string | ((ctx: EditorNoteContext) => string);
  isTinted?: boolean;
  colorVariant?: React.ComponentProps<typeof Note>['colorVariant'];
  automationId?: string;
  /**
   * Whether the live region below already carries this note's text. A screen
   * reader announces the region, so the visible copy stays out of the
   * accessibility tree -- the sentence is reachable once, through the region,
   * and reading the same line twice is what a duplicated copy sounds like.
   */
  isTextInLiveRegion?: boolean;
  shouldShow: (ctx: EditorNoteContext) => boolean;
};

// Read-only notes shown above the editor. The first entry whose `shouldShow`
// returns true is rendered.
//
// ORDER MATTERS: array order encodes precedence, so each predicate only states
// its own condition. Agent-busy is checked first, so the restriction notes
// don't need to re-exclude it; branch is checked before locale, so locale
// doesn't need to exclude branch. Reordering these entries changes which note
// wins when more than one applies.
const EDITOR_NOTES: EditorNoteConfig[] = [
  {
    icon: InfoIcon,
    message:
      "AI Assistant is generating this code. You can make changes when it's done.",
    shouldShow: ({isAgentBusy}) => isAgentBusy,
  },
  // CODE-3501: code components are global to the project and can't be edited on
  // a branch.
  {
    icon: LockIcon,
    isTinted: true,
    automationId: 'vcc-code-editor-branch-note',
    message:
      "Code components are global to the project and can't be edited on a branch. Switch to the main branch to make changes.",
    shouldShow: ({canEditCodeComponents, isBranchMode}) =>
      !canEditCodeComponents && isBranchMode,
  },
  // CODE-3890: code components are structural and can only be edited in the
  // primary locale.
  {
    icon: LockIcon,
    isTinted: true,
    automationId: 'vcc-code-editor-locale-note',
    message:
      'Code components can only be edited in the primary locale. Switch to the primary locale to make changes.',
    shouldShow: ({canEditCodeComponents, isOnPrimaryLocale}) =>
      !canEditCodeComponents && !isOnPrimaryLocale,
  },
  // CODE-3890: a role/permission denial on the primary locale's main branch —
  // explain why the pane is locked instead of leaving it silently read-only.
  {
    icon: LockIcon,
    isTinted: true,
    automationId: 'vcc-code-editor-role-note',
    message:
      "You don't have permission to edit this code component. Ask a workspace member with edit access to make changes.",
    shouldShow: ({canEditCodeComponents, isOnPrimaryLocale, isBranchMode}) =>
      !canEditCodeComponents && isOnPrimaryLocale && !isBranchMode,
  },
  /**
   * After the locks above and before the rebuild note below: a refusal is the
   * person's own unfinished work, and the rebuild saving a sibling file just
   * triggered comes and goes while the refusal is still unresolved. Letting the
   * transient note win there is what makes this one flicker off and back on.
   *
   * A note rather than a toast so the reason stays on screen until the file
   * would save again. A toast that expires leaves the pane looking like an
   * ordinary unsaved edit, with nothing to say why saving keeps doing nothing.
   */
  {
    icon: WarningTriangleIcon,
    colorVariant: 'warning',
    isTinted: true,
    automationId: 'vcc-code-editor-save-refused-note',
    isTextInLiveRegion: true,
    message: ({saveRejection}) => saveRejection ?? '',
    shouldShow: ({saveRejection}) => saveRejection !== null,
  },
  // Checked last: `jobStatus` is site-wide, so if a rebuild happens to overlap
  // one of the permission notes above, the permanent explanation should win
  // over a transient one.
  {
    icon: InfoIcon,
    automationId: 'vcc-code-editor-rebuilding-note',
    message: "Rebuilding the component. You can edit again when it's done.",
    shouldShow: ({isRebuilding}) => isRebuilding,
  },
];

interface Props {
  isSelectable?: boolean;
  /**
   * The multi-file source tree, rendered as a column inside this editor's Card
   * so both sit under one header. Omitted in the single-file view.
   */
  sourceTree?: React.ReactNode;
}

export const CodeComponentEditor = ({isSelectable, sourceTree}: Props) => {
  const dispatch = useCodeComponentDispatch();
  const componentId = useCodeComponentSelector(
    selectComponentCanvasComponentId
  );
  const componentSource = useCodeComponentSelector(
    selectComponentCanvasCodeComponentSource
  );
  // Non-null when a file is selected in the VCC source-file tree (multi-file).
  const activeSourceFile = useCodeComponentSelector(selectActiveSourceFile);
  const codeComponentUpdated = useCodeComponentUpdated();
  const sourceFileContentSaved = useSourceFileContentSaved();
  const streamState = useCodeComponentSelector(selectCodeStreamingState);
  const isAgentTurnInProgress = useCodeComponentSelector(
    selectIsAgentTurnInProgress
  );
  const siteId = useDesignerSelector(selectSiteId);
  const workspaceId = useDesignerSelector(selectWorkspaceId);
  const userId = useDesignerSelector(selectUserId);
  // The ability already blocks editing outside the primary locale (CODE-3890)
  // and on a branch (CODE-3501); the locale and branch reads below feed
  // EDITOR_NOTES so we can show a precise message explaining which restriction
  // applies.
  const canEditCodeComponents = useDesignerSelector(
    selectDesignerAbility('canEditCodeComponents')
  );
  const isOnPrimaryLocale = useDesignerSelector(
    selectIsOnPrimaryLocaleFromStores
  );
  const isBranchMode = useDesignerSelector(selectIsBranchMode);
  const editorTheme = useSpringCm6Theme();
  const instantApplyHighlight = useCodeComponentSelector(
    selectVCCInstantApplyHighlight
  );
  const selectedSourceRange = useCodeComponentSelector(
    selectVCCSelectedSourceRange
  );

  // When a source file is selected in the VCC file tree, the editor shows that
  // file's content; otherwise it shows the canvas-focused component's own
  // source (the default single-file view).
  const existingCode = activeSourceFile
    ? activeSourceFile.content
    : componentSource?.code || '';
  const formatParser = activeSourceFile
    ? getSourceFileFormatParser(activeSourceFile.name)
    : 'tsx';
  const [code, setCode] = useState(existingCode);
  // Keyed on the file on screen, not on `activeSourceFileKey`: a key left
  // pointing at a deleted file would otherwise draft against a missing record.
  const activeDraftKey = activeSourceFile
    ? sourceFileKey(activeSourceFile)
    : mainSourceDraftKey(componentId ?? '');
  const sourceFileDrafts = useCodeComponentSelector(selectSourceFileDrafts);
  // Through a ref because the map changes on every keystroke: as a dependency
  // it would re-run the seeding effect mid-typing and overwrite the buffer.
  const sourceFileDraftsRef = useRef(sourceFileDrafts);
  // Declared before the seeding effect on purpose: effects run in declaration
  // order, so the seed always reads a map fresh for this commit.
  useEffect(() => {
    sourceFileDraftsRef.current = sourceFileDrafts;
  });
  const [isFollowingStream, setIsFollowingStream] = useState(true);
  const sourceTreePeek = useSourceTreePeek();

  const draftSaveInput = useCodeComponentSelector(
    selectCodeComponentDraftSaveInput
  );

  const isStreaming =
    streamState.isStreaming && streamState.componentId === componentId;
  // The component's own source, either mid-stream -- the agent's output, not
  // the user's -- or with no component to save it to. Drafting either would
  // offer to save something that should never be written back.
  const isBufferUndraftable =
    activeSourceFile === null && (isStreaming || componentId === undefined);
  // The seeding effect settles the buffer being left, not the one this
  // describes by then.
  const wasBufferUndraftableRef = useRef(isBufferUndraftable);

  /**
   * Pushes what CodeMirror currently holds into the store as the active file's
   * draft. `onChange` is debounced by 200ms, so anything deciding whether work
   * is unsaved has to ask for this first or it reads a moment-old answer.
   */
  const flushActiveDraft = useCallback((): Record<string, string> => {
    if (isBufferUndraftable) return draftSaveInput.drafts;
    const editor = editorRef.current;
    const live = editor ? editor.state.doc.toString() : code;
    editor?.cancelPendingChange();
    // cancelPendingChange drops the trailing onChange that would otherwise
    // call setCode(live) -- resync here so a later Save reads what the editor
    // is actually showing instead of a stale `code`.
    if (live !== code) setCode(live);
    dispatch(
      codeComponentSourceDraftChanged({
        key: activeDraftKey,
        content: live === existingCode ? null : live,
      })
    );
    // Echoes back what was just dispatched. Nothing keeps this -- the store is
    // still the only copy -- but a caller in the same tick cannot re-read it
    // through a selector, whose value it closed over before the dispatch.
    const {[activeDraftKey]: _replaced, ...rest} = draftSaveInput.drafts;
    return live === existingCode ? rest : {...rest, [activeDraftKey]: live};
  }, [
    dispatch,
    activeDraftKey,
    existingCode,
    code,
    draftSaveInput.drafts,
    isBufferUndraftable,
  ]);

  // The leave guards run outside React and have no other way to make the editor
  // report what it has not reported yet.
  useEffect(
    () => registerCodeComponentDraftFlush(flushActiveDraft),
    [flushActiveDraft]
  );

  // Every user edit goes through here so the draft moves with the buffer --
  // that is what survives a row switch. Matching the saved file drops it.
  const updateCode = useCallback(
    (nextCode: string) => {
      setCode(nextCode);
      if (isBufferUndraftable) return;
      dispatch(
        codeComponentSourceDraftChanged({
          key: activeDraftKey,
          content: nextCode === existingCode ? null : nextCode,
        })
      );
    },
    [dispatch, activeDraftKey, existingCode, isBufferUndraftable]
  );

  const editorRef = useRef<CodeMirror | null>(null);
  const lastScrollPositionRef = useRef(0);
  const hasFiredRealEditRef = useRef(false);

  // Lock the editor whenever the agent is processing (thinking or streaming)
  // to prevent user edits that would be overwritten when streaming begins
  const isAgentBusy = isStreaming || isAgentTurnInProgress;
  // Doesn't say who queued it -- isAgentBusy above still decides which note
  // wins when an agent write is the cause.
  const isRebuilding = useCodeComponentSelector(selectIsSandboxRebuilding);
  // Lock the editor while the agent is busy, a rebuild the user triggered is
  // running, or the user lacks edit permission (e.g. viewing a secondary
  // locale — CODE-3890). A locked editor also disables the save button via
  // `isSaveDisabled` below.
  // Source files opened from the tree are editable: `saveComponent` routes them
  // to VCC_SOURCE_FILE_CONTENT_SAVED, which (unlike the instruction action it
  // used to reuse) is not in MultiplayerStore's ignoredActions, so the edit
  // generates a realtime operation and survives a reload.
  const isEditorReadOnly =
    isAgentBusy || isRebuilding || !canEditCodeComponents;
  /**
   * Files the write rules refused. The store prunes this to the keys whose
   * draft is still there, so a file leaves the list by being written; see
   * `refusedSaveReason` for why only the keys are held.
   */
  const refusedSourceFileKeys = useCodeComponentSelector(
    selectRefusedSourceFileKeys
  );
  const refusalVersion = useCodeComponentSelector(
    selectSourceFileSaveRefusalVersion
  );
  /**
   * Fires on a refusal and nothing else. The version is the event; the list is
   * only what the event acted on. A refused file leaves that list by having its
   * draft dropped, which also happens when an edit lands back on exactly what is
   * stored -- moving the editor then is a jump nobody asked for, mid-keystroke.
   */
  const handledRefusalVersionRef = useRef(refusalVersion);
  const selectFile = useCodeComponentSourceFileSelected();
  useEffect(() => {
    if (handledRefusalVersionRef.current === refusalVersion) return;
    handledRefusalVersionRef.current = refusalVersion;
    /**
     * Refusing cancels the navigation that caused it, so the file on screen is
     * kept when it is one of the refused ones. Otherwise open the first file
     * still waiting, so a leave guard's refusal is never silent. Each later
     * attempt walks to the next one.
     */
    if (refusedSourceFileKeys.length === 0) return;
    if (refusedSourceFileKeys.includes(activeDraftKey)) return;
    selectFile(refusedSourceFileKeys[0] as string);
  }, [refusalVersion, refusedSourceFileKeys, activeDraftKey, selectFile]);
  const saveRejection = refusedSaveReason({
    refusedKeys: refusedSourceFileKeys,
    activeKey: activeDraftKey,
    file: activeSourceFile,
    content: code,
  });
  // The single note to show above the editor — the first whose predicate
  // matches, in precedence order (see EDITOR_NOTES).
  const noteContext: EditorNoteContext = {
    isAgentBusy,
    isRebuilding,
    canEditCodeComponents,
    isBranchMode,
    isOnPrimaryLocale,
    saveRejection,
  };
  const activeNote = EDITOR_NOTES.find((note) => note.shouldShow(noteContext));
  const noteMessage = activeNote?.message;
  const activeNoteMessage =
    typeof noteMessage === 'function' ? noteMessage(noteContext) : noteMessage;
  const [isFormatting, setIsFormatting] = useState(false);
  const [syntaxError, setSyntaxError] = useState<SyntaxError | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const jobStatus = useCodeComponentSelector(selectSandboxJobStatus);

  const hasUnsavedChanges = code !== existingCode;
  const isSaveDisabled = !hasUnsavedChanges || isEditorReadOnly || isFormatting;

  const scrollToLatest = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;

    setIsFollowingStream(isStreaming);
    editor.dispatch({
      effects: EditorView.scrollIntoView(editor.state.doc.length, {y: 'end'}),
    });
  }, [isStreaming]);

  const handleScroll = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const {scroller} = editor;
    if (isStreaming) {
      const delta = scroller.scrollTop - lastScrollPositionRef.current;
      if (delta < 0) {
        // If the user is scrolling up, stop following the stream
        setIsFollowingStream(false);
      } else {
        // CodeMirror scrolls to the last line, which might not be perfectly at 0px from
        // the bottom, so compensate with a small padding
        const bottomPadding = 4;
        const isAtBottom =
          scroller.scrollTop + scroller.clientHeight >=
          scroller.scrollHeight - bottomPadding;
        setIsFollowingStream(isAtBottom);
      }
    }

    lastScrollPositionRef.current = scroller.scrollTop;
  }, [isStreaming]);

  const handleContentChange = useCallback(
    (editor: CodeMirror) => {
      // Auto-scroll to follow streaming code
      if (!isStreaming || !isFollowingStream) return;
      const {scroller} = editor;
      const scrollLeft = scroller.scrollLeft;

      editor.dispatch({
        effects: EditorView.scrollIntoView(editor.state.doc.length, {y: 'end'}),
      });

      // Keep horizontal position stable while following streamed output vertically.
      scroller.scrollLeft = scrollLeft;
      requestAnimationFrame(() => (scroller.scrollLeft = scrollLeft));
    },
    [isStreaming, isFollowingStream]
  );

  // Whether the syntax error modal was triggered by a save action
  // so "Prettify anyway" knows whether to also save after formatting.
  const pendingSaveRef = useRef(false);

  const saveComponent = useCallback(
    (codeToSave: string) => {
      if (codeToSave !== code) {
        updateCode(codeToSave);
      }
      // A source file selected in the tree saves to SourceFilesStore instead
      // of the canvas-focused component's own source.
      if (activeSourceFile) {
        /**
         * Nothing to write, so nothing to refuse. Cmd+S carries no dirty check
         * (the button is disabled without changes), so an idle save of a file
         * whose stored content already breaks a rule would list it as refused
         * with no unsaved work in it, and a later leave guard would walk to that
         * file instead of the one that has work.
         */
        if (codeToSave === existingCode) return;
        const refusal = sourceFileSaveRefusal(activeSourceFile, codeToSave);
        if (refusal) {
          /**
           * The store would take the write and the multiplayer server would
           * drop it with no message back. Refusing here is what shows the
           * person editing why the file will not save; the draft keeps the
           * typed content until they shorten it, and the note above the editor
           * carries the reason from here on. The draft's own drop is what
           * clears it again on a later save.
           */
          // Emitted even if `componentId` is absent: the refusal is what the
          // rollout watch counts, and `violation` with `surface` identifies it.
          trackEvent({
            name: ANALYTICS_EVENTS.VCC_SOURCE_FILE_SAVE_REFUSED,
            data: {
              componentId,
              siteId,
              workspaceId: workspaceId ?? undefined,
              userId,
              extension: extensionOf(activeSourceFile.name),
              // `violation`, not `reason`: the refusal's own `reason` is the
              // message shown to the person, and that names the file.
              violation: refusal.violation,
              surface: 'editor_save',
            },
          });
          dispatch(
            codeComponentSourceFileSaveRefused({
              keys: [sourceFileKey(activeSourceFile)],
            })
          );
          return;
        }
        // Only a `code`-kind file queues a sandbox rebuild (RemoteSandboxStore
        // gates on it); a skill/rule file sharing this component's folder
        // wouldn't flip jobStatus, so isSaving would just reset itself back
        // on the very next render.
        if (activeSourceFile.kind === 'code') setIsSaving(true);
        sourceFileContentSaved({...activeSourceFile, content: codeToSave});
        // Counted at dispatch: the dispatch is fire-and-forget, so the write's
        // outcome only shows up in multiplayer.source_files.sync server-side.
        // Cmd+S carries no dirty check, so an idle re-save would report a write
        // the reducer no-ops on. Count the condition the reducer commits on.
        if (componentId && codeToSave !== existingCode) {
          trackEvent({
            name: ANALYTICS_EVENTS.VCC_SOURCE_FILE_SAVED,
            data: {
              componentId,
              siteId,
              workspaceId: workspaceId ?? undefined,
              userId,
              extension: extensionOf(activeSourceFile.name),
            },
          });
        }
        return;
      }
      if (!componentId) return;
      codeComponentUpdated({code: codeToSave, componentId});
      setIsSaving(true);
      trackEvent({
        name: ANALYTICS_EVENTS.VCC_COMPONENT_SAVED,
        data: {
          componentId,
          siteId,
          workspaceId: workspaceId ?? undefined,
          userId,
        },
      });
    },
    [
      activeSourceFile,
      code,
      existingCode,
      componentId,
      updateCode,
      codeComponentUpdated,
      sourceFileContentSaved,
      siteId,
      workspaceId,
      userId,
    ]
  );

  const startFromCodeEnabled = useFeatureFlag(
    FEATURE_FLAGS.AICC_START_FROM_CODE
  );
  const takenDisplayNames = useTakenComponentNames(componentId);
  const getEditorSource = useCallback(
    () => editorRef.current?.state.doc.toString() ?? code,
    [code]
  );
  const applyPasteHarness = useCallback(
    (nextSource: string) => {
      const editor = editorRef.current;
      if (editor) {
        editor.dispatch({
          changes: {from: 0, to: editor.state.doc.length, insert: nextSource},
          annotations: skipOnChange.of(true),
        });
      }
      saveComponent(nextSource);
    },
    [saveComponent]
  );
  const pasteHarness = usePasteAutoHarness({
    isEnabled:
      startFromCodeEnabled && !isEditorReadOnly && activeSourceFile === null,
    componentId,
    takenDisplayNames,
    getSource: getEditorSource,
    applySource: applyPasteHarness,
    analytics: {siteId, workspaceId: workspaceId ?? undefined, userId},
  });

  const applyFormat = useCallback(
    async (andSave: boolean, parser: NonNullable<typeof formatParser>) => {
      setIsFormatting(true);
      const scrollTop = editorRef.current?.scroller.scrollTop;
      try {
        const formatted = await formatWithPrettier(code, parser);
        if (andSave) {
          saveComponent(formatted);
        } else if (formatted !== code) {
          updateCode(formatted);
        }
      } finally {
        setIsFormatting(false);
        // Restore scroll position after the document is replaced —
        // CodeMirror's overwrite path scrolls the cursor into view which
        // can jump to the top when focus is inside the editor.
        if (scrollTop != null) {
          requestAnimationFrame(() => {
            const scroller = editorRef.current?.scroller;
            if (scroller) {
              scroller.scrollTop = scrollTop;
            }
          });
        }
      }
    },
    [code, saveComponent, updateCode]
  );

  const validateAndFormat = useCallback(
    async (andSave: boolean) => {
      if (!componentId || isEditorReadOnly || isFormatting) return;

      if (formatParser === null) {
        if (andSave) saveComponent(code);
        return;
      }

      setIsFormatting(true);
      const result = await validateSyntax(code, formatParser);
      setIsFormatting(false);

      if (!result.valid) {
        pendingSaveRef.current = andSave;
        setSyntaxError(result.error);
        return;
      }

      applyFormat(andSave, formatParser);
    },
    [
      code,
      componentId,
      isEditorReadOnly,
      isFormatting,
      formatParser,
      applyFormat,
      saveComponent,
    ]
  );

  const formatCode = useCallback(() => {
    validateAndFormat(false);
  }, [validateAndFormat]);

  const formatAndSave = useCallback(() => {
    validateAndFormat(true);
  }, [validateAndFormat]);

  const handleSave = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      formatAndSave();
    },
    [formatAndSave]
  );

  const handleFormatAnyway = useCallback(() => {
    const andSave = pendingSaveRef.current;
    pendingSaveRef.current = false;
    setSyntaxError(null);
    if (formatParser !== null) applyFormat(andSave, formatParser);
  }, [applyFormat, formatParser]);

  const handleCancelFormat = useCallback(() => {
    pendingSaveRef.current = false;
    setSyntaxError(null);
  }, []);

  // ⌘S / Ctrl+S — validate, format, and save via CodeMirror's onSave keybinding
  useEffect(() => {
    const editor = editorRef.current;
    if (editor) {
      editor.config.onSave = formatAndSave;
    }
  }, [formatAndSave]);

  // ⌘⇧P / Ctrl+Shift+P — format only (no save), via CodeMirror's onFormat
  useEffect(() => {
    const editor = editorRef.current;
    if (editor) {
      editor.config.onFormat = formatCode;
    }
  }, [formatCode]);

  // Closing the pane keeps the drafts alive rather than discarding them, so
  // without this the work would sit unsaved and unmentioned until some later
  // navigation asked about an editor that is no longer open.
  //
  // The file on screen is settled first: `onChange` is 200ms behind, so the
  // last keystrokes before the click are not in the draft map yet, and the
  // answer to "is anything unsaved" has to account for them.
  const handleClose = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      const drafts = flushActiveDraft();
      try {
        await ensureCodeChangesConfirmed(
          Object.keys(drafts).length > 0,
          dispatch,
          () => saveCodeComponentDrafts(dispatch, {...draftSaveInput, drafts})
        );
      } catch {
        return;
      }
      dispatch(codeComponentEditorPaneHide());
    },
    [dispatch, flushActiveDraft, draftSaveInput]
  );

  // A different file than last render (row switch, another component, a
  // remount) takes its own draft, so leaving a row keeps what was typed. The
  // same file with new content means a save, a collaborator, or an instant
  // apply landed, and the reducer has already dropped the draft that produced
  // it -- adopt the new content.
  //
  // The outgoing file is settled from CodeMirror's live document rather than
  // from `code`: `onChange` is debounced by 200ms, so a row clicked mid-typing
  // leaves keystrokes owed that would otherwise arrive after the switch and be
  // filed under the file just opened.
  const seededRef = useRef<{key: string; savedCode: string} | null>(null);
  useEffect(() => {
    const seeded = seededRef.current;
    if (seeded === null || seeded.key !== activeDraftKey) {
      const editor = editorRef.current;
      if (seeded !== null && editor && !wasBufferUndraftableRef.current) {
        const live = editor.state.doc.toString();
        editor.cancelPendingChange();
        dispatch(
          codeComponentSourceDraftChanged({
            key: seeded.key,
            content: live === seeded.savedCode ? null : live,
          })
        );
      }
      seededRef.current = {key: activeDraftKey, savedCode: existingCode};
      setCode(sourceFileDraftsRef.current[activeDraftKey] ?? existingCode);
      return;
    }
    seededRef.current = {key: activeDraftKey, savedCode: existingCode};
    setCode(existingCode);
    // The reducer drops a spent draft for every write it can see. This covers
    // the file on screen for the ones it can't, so the tree never marks a row
    // the Save button refuses to clear.
    if (sourceFileDraftsRef.current[activeDraftKey] !== undefined) {
      dispatch(
        codeComponentSourceDraftChanged({key: activeDraftKey, content: null})
      );
    }
  }, [existingCode, activeDraftKey, dispatch]);

  // After the seeding effect on purpose: effects run in declaration order, so
  // the settle above still reads the commit the outgoing buffer belonged to.
  useEffect(() => {
    wasBufferUndraftableRef.current = isBufferUndraftable;
  });

  // A discard clears every draft in the store, but nothing tells the buffer
  // this editor is showing -- CodeMirror still holds what was typed. Keyed on
  // the explicit `draftsClearedVersion` signal rather than inferred from
  // `sourceFileDrafts` losing an entry, which also happens per-file (a save,
  // a rename) and mid-stream. The leave guard's own save bumps this signal
  // too, but is harmless: it already wrote `live` as `existingCode`, so
  // `code !== existingCode` below is false and this is a no-op.
  const draftsClearedVersion = useCodeComponentSelector(
    selectDraftsClearedVersion
  );
  const seenDraftsClearedVersionRef = useRef(draftsClearedVersion);
  useEffect(() => {
    if (seenDraftsClearedVersionRef.current === draftsClearedVersion) return;
    seenDraftsClearedVersionRef.current = draftsClearedVersion;
    if (!isBufferUndraftable && code !== existingCode) {
      editorRef.current?.cancelPendingChange();
      setCode(existingCode);
    }
  }, [draftsClearedVersion, isBufferUndraftable, code, existingCode]);

  // Scroll to and flash the lines an instant text apply just rewrote, so the
  // user sees where in the source their canvas edit landed. Initialized to
  // the store's current version so a pane remount doesn't replay the last
  // apply.
  const consumedHighlightVersionRef = useRef(
    instantApplyHighlight?.version ?? 0
  );
  const highlightClearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  // Scroll position from before the apply's document overwrite lands — the
  // overwrite resets the scroller to the top (same quirk applyFormat works
  // around), and the smooth scroll must start from where the user was, not
  // from the top.
  const preApplyScrollTopRef = useRef<number | null>(null);
  useEffect(() => {
    const highlight = instantApplyHighlight;
    if (!highlight || highlight.version === consumedHighlightVersionRef.current)
      return;
    // The applied source hasn't reached the editor yet — the `setCode` sync
    // above triggers a re-render, and this effect runs again once the
    // CodeEditor (a child, so its effects run first) holds the new document.
    // The scroller still shows the pre-apply position here; remember it.
    if (code !== existingCode || !editorRef.current) {
      preApplyScrollTopRef.current =
        editorRef.current?.scroller.scrollTop ?? null;
      return;
    }
    consumedHighlightVersionRef.current = highlight.version;
    const preApplyScrollTop = preApplyScrollTopRef.current;
    preApplyScrollTopRef.current = null;
    if (preApplyScrollTop != null) {
      editorRef.current.scroller.scrollTop = preApplyScrollTop;
    }

    // RAF so CodeMirror has measured the freshly replaced document before we
    // read line geometry for the scroll target.
    requestAnimationFrame(() => {
      const editor = editorRef.current;
      if (!editor) return;
      const doc = editor.state.doc;
      const linePos = doc.line(Math.min(highlight.fromLine, doc.lines)).from;

      editor.dispatch({effects: setInstantApplyHighlight.of(highlight)});

      // Smooth-scroll the changed line to ~35% from the top of the pane
      // (mirrors the code editor reveal in the design prototype).
      const view = EditorView.findFromDOM(editor.scroller);
      if (view) {
        const {scroller} = editor;
        view.requestMeasure({
          read: () => view.lineBlockAt(linePos).top,
          write: (top) => {
            // Undo the overwrite's scroll-to-top once more — CodeMirror's own
            // measure cycle can re-apply it between the effect and this write.
            if (preApplyScrollTop != null) {
              scroller.scrollTop = preApplyScrollTop;
            }
            scroller.scrollTo({
              top: Math.max(0, top - scroller.clientHeight * 0.35),
              behavior: 'smooth',
            });
          },
        });
      } else {
        editor.dispatch({
          effects: EditorView.scrollIntoView(linePos, {y: 'center'}),
        });
      }

      // Drop the decorations once the flash has faded — a stale decoration
      // would replay its animation whenever CodeMirror re-creates the line's
      // DOM (scrolling out of and back into the viewport).
      if (highlightClearTimeoutRef.current) {
        clearTimeout(highlightClearTimeoutRef.current);
      }
      highlightClearTimeoutRef.current = setTimeout(() => {
        editorRef.current?.dispatch({
          effects: setInstantApplyHighlight.of(null),
        });
      }, INSTANT_APPLY_HIGHLIGHT_DURATION_MS);
    });
  }, [instantApplyHighlight, code, existingCode]);

  // Reveal the source of the element the user just selected on the canvas
  // (EXT-870): tint its lines and, when they're off screen, scroll them into
  // view. Initialized to the store's current version so opening the pane doesn't
  // yank the viewport to whatever happened to be selected.
  const consumedSelectionVersionRef = useRef(selectedSourceRange?.version ?? 0);
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    if (!selectedSourceRange) {
      editor.dispatch({effects: setSelectedElementHighlight.of(null)});
      // Forget the consumed version alongside the store's. The counter lives on the
      // range object, so a clear drops it and the next reveal starts over at 1 —
      // remembering a 1 here would make that reveal look already-consumed and it
      // would never tint or scroll. Selecting an unstamped element declines, which
      // clears, so this is the ordinary path and not an edge case.
      consumedSelectionVersionRef.current = 0;
      return;
    }
    if (selectedSourceRange.version === consumedSelectionVersionRef.current) {
      return;
    }
    // The stamp is only meaningful against the source it was built from; while an
    // edit is in flight to the document, wait for it to settle.
    if (code !== existingCode) return;
    consumedSelectionVersionRef.current = selectedSourceRange.version;

    editor.dispatch({
      effects: setSelectedElementHighlight.of(selectedSourceRange),
    });

    // Never fight the user for the viewport: typing wins, and a target already on
    // screen needs no scroll (clicking around the canvas shouldn't jitter the
    // pane).
    if (editor.isTyping) return;

    const view = EditorView.findFromDOM(editor.scroller);
    if (!view) return;
    const doc = editor.state.doc;
    const linePos = doc.line(
      Math.min(selectedSourceRange.fromLine, doc.lines)
    ).from;
    const {scroller} = editor;
    view.requestMeasure({
      read: () => view.lineBlockAt(linePos),
      write: (block) => {
        const viewTop = scroller.scrollTop;
        const viewBottom = viewTop + scroller.clientHeight;
        if (block.top >= viewTop && block.bottom <= viewBottom) return;
        scroller.scrollTo({
          top: Math.max(
            0,
            block.top - scroller.clientHeight * REVEAL_VIEWPORT_FRACTION
          ),
          behavior: 'smooth',
        });
      },
    });
  }, [selectedSourceRange, code, existingCode]);

  useEffect(() => {
    return () => {
      if (highlightClearTimeoutRef.current) {
        clearTimeout(highlightClearTimeoutRef.current);
      }
    };
  }, []);

  // Reset the once-per-session real-edit guard when switching components
  useEffect(() => {
    hasFiredRealEditRef.current = false;
    setIsSaving(false);
  }, [componentId]);

  useEffect(() => {
    if (isSaving && jobStatus === 'IDLE') {
      setIsSaving(false);
    }
  }, [isSaving, jobStatus]);

  const handleCodeChange = useCallback(
    (newCode: string) => {
      updateCode(newCode);
      if (
        !hasFiredRealEditRef.current &&
        !isStreaming &&
        !isAgentTurnInProgress &&
        componentId &&
        newCode !== existingCode
      ) {
        hasFiredRealEditRef.current = true;
        trackEvent({
          name: ANALYTICS_EVENTS.VCC_REAL_EDIT,
          data: {
            componentId,
            siteId,
            workspaceId: workspaceId ?? undefined,
            userId,
          },
        });
      }
    },
    [
      updateCode,
      isStreaming,
      isAgentTurnInProgress,
      componentId,
      existingCode,
      siteId,
      workspaceId,
      userId,
    ]
  );

  // Update the code when the stream code changes.
  //
  // Only while the main source is in view. The stream carries the component's
  // `.webflow.tsx`, and `isStreaming` says nothing about which row the tree has
  // selected -- so with a sub-file open this would fill the buffer with the
  // component's source. Sub-files became editable in this PR, so that buffer is
  // now saveable: `existingCode` is unchanged, the `[existingCode]` sync below
  // never fires to correct it, and Save would write the component source over
  // the module.
  //
  // Not through `updateCode`: the agent persists its own output, and a draft
  // here would offer to save a half-written stream on the way out.
  useEffect(() => {
    if (activeSourceFile !== null) return;
    if (isStreaming && streamState.code !== null) {
      setCode(streamState.code);
    }
  }, [activeSourceFile, isStreaming, streamState.code]);

  // Automatically follow the stream when it starts
  useEffect(() => {
    setIsFollowingStream(isStreaming);
  }, [isStreaming]);

  return (
    <>
      <PrettifySyntaxErrorModal
        isOpen={syntaxError != null}
        language={syntaxError?.language ?? ''}
        line={syntaxError?.line}
        column={syntaxError?.column}
        message={syntaxError?.message ?? ''}
        onFormatAnyway={handleFormatAnyway}
        onCancel={handleCancelFormat}
      />
      <Card isSeamless elevation={1} style={cardStyle}>
        <View
          data-code-component-editor-drag-handle
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          borderBottom="$borderDefault"
          padding="6px $2"
          cursor="move"
        >
          <View display="flex" alignItems="center" gap="$1" minWidth={0}>
            {sourceTree && (
              <IconButton
                // Hidden, the button expands the sidebar; showing, it points
                // at the panel it closes.
                Icon={
                  sourceTreePeek.isCollapsed
                    ? SidebarExpandIcon
                    : SidebarCollapseIcon
                }
                aria-controls={SOURCE_TREE_ID}
                aria-expanded={!sourceTreePeek.isCollapsed}
                data-automation-id="vcc-source-tree-toggle"
                label={sourceTreePeek.isCollapsed ? 'Show files' : 'Hide files'}
                onClick={sourceTreePeek.toggleCollapsed}
                variant="ghost"
                withTooltip
              />
            )}
            <Text
              truncateToLines={1}
              as="h2"
              font="title"
              colorVariant="primary"
              userSelect="none"
            >
              Edit Code Component
            </Text>
          </View>
          <View display="flex" alignItems="center" gap="$1">
            <Button
              colorVariant={isSaveDisabled ? 'default' : 'primary'}
              variant="solid"
              onClick={handleSave}
              isDisabled={isSaveDisabled}
            >
              {isSaveDisabled ? (isSaving ? 'Saving...' : 'Saved') : 'Save'}
            </Button>
            <IconButton
              label="Close code editor"
              Icon={CloseDefaultIcon}
              variant="ghost"
              onClick={handleClose}
            />
          </View>
        </View>
        {/*
          Mounted whether or not there is a note to show. A screen reader
          announces a change to a live region that is already in the DOM, not
          the arrival of one carrying its text, and this is the only feedback a
          refused save gets -- `Note` maps `warning` to no live region at all.
        */}
        <VisuallyHidden role="status" aria-live="polite">
          {saveRejection ?? ''}
        </VisuallyHidden>
        {activeNote && (
          <View
            data-automation-id={activeNote.automationId}
            padding="$2"
            borderBottom="$borderDefault"
            background="$backgroundPrimary"
          >
            <Note
              icon={activeNote.icon}
              colorVariant={activeNote.colorVariant}
              isTinted={activeNote.isTinted}
            >
              <NoteBody
                aria-hidden={activeNote.isTextInLiveRegion ? true : undefined}
              >
                {activeNoteMessage}
              </NoteBody>
            </Note>
          </View>
        )}
        {pasteHarness.notice && (
          <PasteHarnessNotice
            onDismiss={pasteHarness.dismiss}
            plan={pasteHarness.notice.plan}
          />
        )}
        {/* A hover peek retracts from three places, and it needs all three: the
            tree's own mouseleave once the pointer has been on it, the editor
            column below for a pointer that crossed the strip and carried on
            past the tree, and this row for a pointer that left the pane without
            touching either. The strip itself can't do it — it sits under the
            tree, so the sliding tree covering the pointer would fire its
            mouseleave and flicker the peek shut. */}
        <EditorBodyRow
          data-locked={!isSelectable}
          onMouseLeave={sourceTreePeek.endPeek}
        >
          {sourceTree && (
            <SourceTreeSlot
              data-collapsed={sourceTreePeek.isCollapsed}
              data-peeking={sourceTreePeek.isPeeking}
              id={SOURCE_TREE_ID}
              onMouseLeave={sourceTreePeek.endPeek}
              onPointerDownCapture={sourceTreePeek.pinPeek}
            >
              {sourceTree}
            </SourceTreeSlot>
          )}
          <View
            position="relative"
            flex={1}
            flexGrow={1}
            minWidth={0}
            overflowY="auto"
            background="$background1"
            onMouseEnter={sourceTreePeek.endPeek}
            // Dismissal lives here rather than on the row so that a press inside
            // the tree cannot reach it: on the row it ran before the tree's own
            // pinPeek (capture is outer to inner) and the pin only survived
            // because React batched the two writes.
            onPointerDownCapture={
              sourceTreePeek.isPeeking ? sourceTreePeek.dismissPeek : undefined
            }
          >
            <CodeEditor
              editorRef={editorRef}
              value={code}
              onChange={handleCodeChange}
              // A selected sub-file picks its grammar from its extension.
              language={activeSourceFile ? undefined : 'TSX'}
              fileName={activeSourceFile?.name}
              lineNumbers
              lineWrapping={false}
              highlightStyle="wf-code"
              theme={editorTheme}
              indentMarkers
              style={codeEditorStyle}
              extensions={codeEditorExtensions}
              readOnly={isEditorReadOnly}
              valueChangeMode={isStreaming ? 'append' : 'overwrite'}
              onContentChange={handleContentChange}
              onScroll={handleScroll}
            />
            {isStreaming && !isFollowingStream && (
              <View
                background="$background1"
                position="absolute"
                bottom="$4"
                right="$4"
              >
                <IconButton
                  withTooltip
                  label="Jump to latest"
                  Icon={ArrowDownIcon}
                  onClick={scrollToLatest}
                />
              </View>
            )}
          </View>
          {sourceTree && sourceTreePeek.isCollapsed && (
            <SourceTreePeekZone onMouseEnter={sourceTreePeek.startPeek} />
          )}
        </EditorBodyRow>
      </Card>
    </>
  );
};
